import type { FootprintProps } from '@onefootprint/footprint-js-legacy';
import { FootprintPrivateEvent, FootprintPublicEvent } from '@onefootprint/footprint-js-legacy';
import Postmate from '@onefootprint/postmate';

import { getLogger } from '@/idv/utils';
import type {
  CompletePayload,
  CustomChildAPI,
  IframeAdapterReturn,
  RequirementChangedPayload,
  ResizedPayload,
} from '../types';
import generateEventEmitter from '../utils/generate-event-emitter';
import { setClientVersionHeaderFromPostmate, setClientVersionHeaderFromUrl } from './set-client-version-header';

const { auth, canceled, closed, completed, relayToComponents } = FootprintPublicEvent;
const { propsReceived, resized, started, relayFromComponents } = FootprintPrivateEvent;
const { logError, logInfo, logWarn } = getLogger({
  location: 'bifrost-iframe-adapter',
});

const getSpecificEvent = (event: string, childRef: CustomChildAPI | null): string => {
  const sdkInitId = childRef?.model?.initId;
  return sdkInitId?.length ? `${sdkInitId}:${event}` : event;
};

const generateIframeAdapter = (): IframeAdapterReturn => {
  let isAdapterLoaded: boolean = false;
  let loadPromise: Promise<CustomChildAPI | null> | null = null;
  let postmateChildApiRef: CustomChildAPI | null = null;
  const eventEmitter = generateEventEmitter();
  const hasClientVersionFromUrl = setClientVersionHeaderFromUrl();

  const sendEvent = (event: string, data?: unknown): void => {
    const specificEvent = getSpecificEvent(event, postmateChildApiRef);
    if (postmateChildApiRef) {
      // Not logged per dispatch: `resized` fires once per pixel of an inline embed's height animation.
      postmateChildApiRef.emit(specificEvent, data);
    } else {
      logWarn(`Footprint.js must be initialized in order to dispatch the event "${event}"`);
    }
  };

  const doLoad = async (): Promise<CustomChildAPI | null> => {
    if (isAdapterLoaded) {
      return Promise.resolve(postmateChildApiRef);
    }

    const contextModel = {
      [propsReceived]: (props: FootprintProps) => {
        eventEmitter.emit(propsReceived, props);
      },
      [relayFromComponents]: () => {
        eventEmitter.emit(relayFromComponents);
      },
      back: () => {
        eventEmitter.emit('back');
      },
    };

    try {
      postmateChildApiRef = await new Postmate.Model(contextModel);
      logInfo('Starting footprint from iframe adapter');
      postmateChildApiRef.emit(started);
      isAdapterLoaded = true;

      if (!hasClientVersionFromUrl) {
        setClientVersionHeaderFromPostmate(postmateChildApiRef?.model?.sdkVersion);
      }

      return postmateChildApiRef;
    } catch (err) {
      isAdapterLoaded = true;
      logError('Footprint.js handshake reply failed', err);

      return null;
    }
  };

  // A second Postmate.Model would also answer the parent handshake and register duplicate
  // listeners, making every parent->child call (e.g. back) run twice.
  const load = (): Promise<CustomChildAPI | null> => {
    if (!loadPromise) {
      loadPromise = doLoad();
    }
    return loadPromise;
  };

  const complete = ({ validationToken, delay = 0, autoClose = true }: CompletePayload) => {
    logInfo('Completing footprint from iframe adapter');
    sendEvent(completed, validationToken);
    if (!autoClose) {
      return;
    }
    setTimeout(() => {
      logInfo('Closing footprint after complete timeout from iframe adapter');
      sendEvent(closed);
    }, delay);
  };

  return {
    auth: (validationToken: string) => sendEvent(auth, validationToken),
    backButtonVisibilityChange: (isVisible: boolean) => sendEvent('backButtonVisibilityChange', isVisible),
    requirementChanged: (payload: RequirementChangedPayload) => sendEvent('requirementChanged', payload),
    resized: (payload: ResizedPayload) => sendEvent(resized, payload),
    relayToComponents: (tokens: { authToken: string; vaultingToken: string }) => sendEvent(relayToComponents, tokens),
    cancel: () => sendEvent(canceled),
    close: () => sendEvent(closed),
    complete,
    getAdapterResponse: () => postmateChildApiRef,
    getLoadingStatus: () => isAdapterLoaded,
    load,
    on: eventEmitter.on,
  };
};

export default generateIframeAdapter;
